import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { buildWhatsAppLink, GLOBAL_WHATSAPP_NUMBER, normalizeWhatsAppNumber } from "@/utils/whatsapp";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";

interface WhatsAppSupportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const WhatsAppSupportDialog = ({ open, onOpenChange }: WhatsAppSupportDialogProps) => {
  const [subject, setSubject] = useState("");
  const [urgency, setUrgency] = useState("");
  const [details, setDetails] = useState("");
  const [userInfo, setUserInfo] = useState<{ name: string; email: string; company: string; role?: string; companyId?: number | null } | null>(null);
  const [companyPhone, setCompanyPhone] = useState<string | null>(null);
  const { toast } = useToast();

  // Capturar informações do usuário logado
  useEffect(() => {
    const userData = getUser();
    if (userData) {
      const user = JSON.parse(userData);
      const emailDomain = user.email ? user.email.split("@")[1] : "";
      setUserInfo({
        name: user.name || "Usuário",
        email: user.email || "",
        company: user.company_name || emailDomain || "N/A",
        role: user.role,
        companyId: user.company_id || null
      });
    }
  }, []);

  useEffect(() => {
    const loadCompanyPhone = async () => {
      if (!userInfo?.companyId) return;
      try {
        const token = getToken();
        const response = await fetch(getApiEndpoint(`/api/companies/${userInfo.companyId}`), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data?.company?.phone) {
            setCompanyPhone(data.company.phone);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar telefone da empresa:", error);
      }
    };

    loadCompanyPhone();
  }, [userInfo?.companyId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validação
    if (!subject.trim() || !urgency || !details.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos antes de continuar.",
        variant: "destructive",
      });
      return;
    }

    const isExternal = userInfo?.role === "external";
    const companyDigits = normalizeWhatsAppNumber(companyPhone);
    const targetNumber = isExternal ? (companyDigits || GLOBAL_WHATSAPP_NUMBER) : GLOBAL_WHATSAPP_NUMBER;
    if (isExternal && !companyDigits) {
      toast({
        title: "Telefone não encontrado",
        description: "Telefone da empresa não encontrado. Enviando para o suporte global.",
      });
    }

    // Construir mensagem do WhatsApp
    const message = `Olá! Preciso de suporte técnico.

*Empresa/Domínio:* ${userInfo?.company || "N/A"}
*Solicitante:* ${userInfo?.name || "N/A"}
*Email:* ${userInfo?.email || "N/A"}

*Assunto/Problema:* ${subject}

*Urgência:* ${urgency}

*Detalhes:* ${details}`;

    // Abrir WhatsApp
    window.open(buildWhatsAppLink(targetNumber, message), "_blank");

    // Limpar campos e fechar modal
    setSubject("");
    setUrgency("");
    setDetails("");
    onOpenChange(false);

    toast({
      title: "Redirecionando para o WhatsApp",
      description: "Você será redirecionado para conversar com nosso suporte.",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Suporte via WhatsApp</DialogTitle>
          <DialogDescription>
            Preencha as informações abaixo para iniciar o atendimento com {userInfo?.role === "external" ? "o administrador da sua empresa" : "nosso suporte"}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="subject">
              Assunto/Problema <span className="text-red-500">*</span>
            </Label>
            <Input
              id="subject"
              placeholder="Ex: Computador não liga, Internet lenta, etc."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="urgency">
              Urgência <span className="text-red-500">*</span>
            </Label>
            <Select value={urgency} onValueChange={setUrgency} required>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a urgência" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Crítica">Crítica (Sistema parado)</SelectItem>
                <SelectItem value="Alta">Alta (Impacto significativo)</SelectItem>
                <SelectItem value="Média">Média (Impacto moderado)</SelectItem>
                <SelectItem value="Baixa">Baixa (Sem impacto imediato)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="details">
              Detalhes <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="details"
              placeholder="Descreva com detalhes o que está acontecendo..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={4}
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
              Abrir WhatsApp
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppSupportDialog;
